import type { LeadSource, LeadStatus } from "@/api/leads";
import type { ListFilterDef, ListFilterValues } from "@/hooks/useListFilters";
import { US_STATES } from "@/lib/usStates";

// The Leads list's filter descriptor. Every lead-specific noun on this page
// lives HERE — useListFilters and ListFilterBar carry none of it.

const STATUSES: LeadStatus[] = [
  "New",
  "Contacted",
  "Interested",
  "NotInterested",
  "Unresponsive",
  "HandedOff",
  "Disqualified",
];

const SOURCES: LeadSource[] = [
  "ColdCall",
  "InboundInquiry",
  "TradeShow",
  "Referral",
  "WebForm",
  "SocialMedia",
  "IBBA",
  "MaSource",
  "BizBuySell",
  "LinkedIn",
  "PurchasedList",
  "Other",
  "ExternalGenerator",
];

/** "Archived" is a sentinel in this dropdown — it drives the dedicated archived
 *  filter rather than a LeadStatus (an archived lead is still Disqualified).
 *  Every other value is a real LeadStatus. */
export type LeadStatusFilter = LeadStatus | "" | "Archived";

const STATUS_TOKENS = new Set<string>([...STATUSES, "Archived"]);
const SOURCE_TOKENS = new Set<string>(SOURCES);
const STATE_TOKENS = new Set<string>(US_STATES.map((s) => s.code));

/** Identity helper: pins each filter's value type so the hook infers it back
 *  out, instead of widening a literal union to `string`. */
function filter<V>(def: ListFilterDef<V>): ListFilterDef<V> {
  return def;
}

export const LEAD_FILTERS = {
  /** `q` rather than `search`: the map key IS the query-string param name, and
   *  ?q= is what every other URL-persisted list search in the app already
   *  uses. */
  q: filter<string>({
    control: "search",
    defaultValue: "",
    placeholder: "Search name, company, phone, email…",
    className: "grow",
    // Every keystroke used to re-key both the list and the stats query, so a
    // 12-character name fired 24 requests. 300ms matches the debounce the
    // prequalification panels already use.
    debounceMs: 300,
  }),
  status: filter<LeadStatusFilter>({
    control: "select",
    defaultValue: "",
    label: "Status filter",
    className: "w-40",
    options: [
      { value: "", label: "All statuses" },
      ...STATUSES.map((s) => ({ value: s, label: s })),
      { value: "Archived", label: "Archived" },
    ],
    // An unrecognised token falls back to "all" rather than being forwarded to
    // the server, so a hand-edited or stale URL degrades to the default view.
    parse: (raw) => (STATUS_TOKENS.has(raw) ? (raw as LeadStatusFilter) : undefined),
  }),
  source: filter<LeadSource | "">({
    control: "select",
    // No aria-label: the control has never carried one, and adding a name here
    // would be a change to this page rather than to the shell being proved out.
    defaultValue: "",
    className: "w-40",
    options: [
      { value: "", label: "All sources" },
      ...SOURCES.map((s) => ({ value: s, label: s })),
    ],
    parse: (raw) => (SOURCE_TOKENS.has(raw) ? (raw as LeadSource) : undefined),
  }),
  state: filter<string>({
    control: "select",
    defaultValue: "",
    label: "State filter",
    className: "w-44",
    options: [
      { value: "", label: "All states" },
      ...US_STATES.map((s) => ({ value: s.code, label: `${s.name} (${s.code})` })),
    ],
    parse: (raw) => (STATE_TOKENS.has(raw) ? raw : undefined),
  }),
  /** Set by the CSV import page's "View imported leads" link, cleared by the
   *  scoped-view notice. No control of its own — it is a URL-only filter, which
   *  is exactly why it belongs in the descriptor rather than beside it. */
  importBatchId: filter<string | undefined>({
    control: "hidden",
    defaultValue: undefined,
    parse: (raw) => raw || undefined,
  }),
};

export type LeadFilterValues = ListFilterValues<typeof LEAD_FILTERS>;
